import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * AnalyticsNotice — dismissible bottom-fixed notice announcing that Regulens
 * uses privacy-respecting Google Analytics (anonymized IP, no ad targeting).
 *
 * Model: notice + opt-out (not consent-gate). GA loads by default because
 *   1. We only collect anonymous page-view metrics.
 *   2. IP is anonymized, Google Signals + ad personalization are OFF.
 *   3. Users have a documented one-click opt-out (localStorage flag on
 *      /privacy) that is respected by both the loader script in
 *      public/index.html and the useAnalytics hook.
 *
 * Dismissed state persists in localStorage[regulens_analytics_notice_ack].
 * The banner never re-appears once dismissed — the opt-out link on the
 * Privacy Policy page remains the durable control.
 */
const ACK_KEY = "regulens_analytics_notice_ack";

export default function AnalyticsNotice() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!process.env.REACT_APP_GA_MEASUREMENT_ID) return;
    try {
      if (localStorage.getItem(ACK_KEY) === "1") return;
      // Small delay so the banner doesn't fight the terms/disclaimer modal
      // for attention on first visit.
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    } catch (e) { /* localStorage disabled — leave hidden */ }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(ACK_KEY, "1"); } catch (e) { /* no-op */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-50 rounded-lg border border-[var(--rg-border-hi)] bg-[var(--rg-surface)] shadow-lg p-4"
      data-testid="analytics-notice"
      role="dialog"
      aria-labelledby="analytics-notice-heading"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div
            id="analytics-notice-heading"
            className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-1.5"
          >
            Privacy-respecting analytics
          </div>
          <p className="text-xs text-[var(--rg-text)] leading-relaxed">
            We use Google Analytics 4 with anonymized IPs and no ad-targeting to
            understand aggregate page views only. You can opt out anytime on the{" "}
            <Link
              to="/privacy"
              className="underline text-[var(--rg-sage)] hover:text-[var(--rg-text-strong)]"
              data-testid="analytics-notice-privacy-link"
            >
              Privacy Policy
            </Link>{" "}
            page.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              onClick={dismiss}
              className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold text-xs h-8"
              data-testid="analytics-notice-ack"
            >
              Got it
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-[var(--rg-muted)] hover:text-[var(--rg-text-strong)] -m-1 p-1"
          aria-label="Dismiss analytics notice"
          data-testid="analytics-notice-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
